import api from './api';
import type { Transfer, TransferLine, PaginatedResponse, TransferStatus } from '../types/venue';

const base = (venueId: string) => `/admin/venue/${venueId}/transfer`;

export interface CreateTransferPayload {
  fromLocationId: string;
  toLocationId: string;
  transferredAt?: string | null;
  transferredBy?: string;
  notes?: string;
}

export interface UpdateTransferPayload {
  transferredAt?: string | null;
  transferredBy?: string;
  notes?: string;
}

export interface PostTransferResult {
  transfer: Transfer;
  movementsPosted: number;
}

const transferService = {
  getTransfers: async (
    venueId: string,
    page = 1,
    pageSize = 20,
    status?: TransferStatus,
  ): Promise<PaginatedResponse<Transfer>> => {
    const params: Record<string, unknown> = { page, pageSize };
    if (status) params.status = status;
    const response = await api.get(base(venueId), { params });
    return response.data;
  },

  getTransfer: async (
    venueId: string,
    transferId: string,
  ): Promise<{ transfer: Transfer; lines: TransferLine[] }> => {
    const response = await api.get(`${base(venueId)}/${transferId}`);
    return response.data;
  },

  /** Create an empty draft transfer with from/to scope. */
  createTransfer: async (
    venueId: string,
    data: CreateTransferPayload,
  ): Promise<Transfer> => {
    const response = await api.post(base(venueId), data);
    return response.data;
  },

  /** Update header fields (transferredAt, transferredBy, notes). Draft only. */
  updateTransfer: async (
    venueId: string,
    transferId: string,
    data: UpdateTransferPayload,
  ): Promise<Transfer> => {
    const response = await api.patch(`${base(venueId)}/${transferId}`, data);
    return response.data;
  },

  /** Delete a draft transfer. Not allowed after posting. */
  deleteTransfer: async (venueId: string, transferId: string): Promise<void> => {
    await api.delete(`${base(venueId)}/${transferId}`);
  },

  /**
   * Add a line by fromInventoryItemId and quantity.
   * Item must be in the from-scope. Duplicates rejected.
   * Returns line with currentQty for UI display.
   * Phase A: pass lotId for lot-tracked items; omit for non-lot-tracked.
   */
  addLine: async (
    venueId: string,
    transferId: string,
    fromInventoryItemId: string,
    quantity: number,
    lotId?: string,
  ): Promise<TransferLine> => {
    const response = await api.post(`${base(venueId)}/${transferId}/lines`, {
      fromInventoryItemId,
      quantity,
      ...(lotId != null && { lotId }),
    });
    return response.data;
  },

  /**
   * TRANSFER-BULK.1 — put every item standing at the source onto the transfer.
   *
   * Fills a DRAFT: quantities default to everything on hand and every line
   * stays editable, so this is a starting point rather than a decision. Nothing
   * is posted.
   *
   * Idempotent — items already on the transfer come back as `skipped` rather
   * than duplicated, so it is safe after adding a few by hand.
   */
  bulkLoadLines: async (
    venueId: string,
    transferId: string,
  ): Promise<{ linesCreated: number; skipped: number; message?: string }> => {
    const response = await api.post(`${base(venueId)}/${transferId}/lines/bulk-load`);
    return response.data;
  },

  /** Update quantity on a line. Draft only. */
  updateLine: async (
    venueId: string,
    transferId: string,
    lineId: string,
    quantity: number,
    lotId?: string,
  ): Promise<TransferLine> => {
    const response = await api.patch(`${base(venueId)}/${transferId}/lines/${lineId}`, {
      quantity,
      ...(lotId != null && { lotId }),
    });
    return response.data;
  },

  /** Remove a line from the transfer. Draft only. */
  removeLine: async (
    venueId: string,
    transferId: string,
    lineId: string,
  ): Promise<void> => {
    await api.delete(`${base(venueId)}/${transferId}/lines/${lineId}`);
  },

  /**
   * Post the transfer.
   * Creates TRANSFER_OUT + TRANSFER_IN movements for each line.
   * Updates InventoryBalance on both sides. Atomic (MongoDB transaction).
   * One-way action — transfer becomes immutable after this.
   */
  postTransfer: async (
    venueId: string,
    transferId: string,
  ): Promise<PostTransferResult> => {
    const response = await api.post(`${base(venueId)}/${transferId}/post`);
    return response.data;
  },
};

export default transferService;
